import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMusic } from '../contexts/MusicContext';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import TrackSearch from '../components/TrackSearch';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Play, Pause, Clock, Music, ArrowLeft, Plus, X, GripVertical, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortableTrack = ({ track, index, onRemove, onPlay, isCurrentlyPlaying, formatDuration }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: track.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`track-item group ${isCurrentlyPlaying ? 'playing' : ''}`}
      data-testid={`track-item-${index}`}
    >
      {/* Drag handle — desktop only. Mobile uses tap-to-play and the dedicated edit flow. */}
      <button {...attributes} {...listeners} className="hidden md:flex cursor-grab active:cursor-grabbing p-1 hover:bg-white/10 rounded" data-testid={`drag-handle-${index}`}>
        <GripVertical className="w-4 h-4 text-gray-500" />
      </button>

      {/* Track index / playing indicator */}
      <span className="w-5 md:w-6 text-center text-gray-500 cursor-pointer text-[11px] md:text-sm flex-shrink-0" onClick={() => onPlay(index)}>
        {isCurrentlyPlaying ? (
          <span className="flex gap-0.5 items-end justify-center h-4">
            <span className="w-1 bg-[#FF007F] animate-pulse" style={{ height: '60%' }} />
            <span className="w-1 bg-[#FF007F] animate-pulse" style={{ height: '100%', animationDelay: '0.2s' }} />
            <span className="w-1 bg-[#FF007F] animate-pulse" style={{ height: '40%', animationDelay: '0.4s' }} />
          </span>
        ) : (
          <>
            <span className="group-hover:hidden">{index + 1}</span>
            <Play className="w-4 h-4 hidden group-hover:block mx-auto text-white" fill="currentColor" />
          </>
        )}
      </span>

      {/* Cover + title + artist */}
      <div className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer" onClick={() => onPlay(index)}>
        {track.album_image && <img src={track.album_image} alt={track.album} className="w-9 h-9 md:w-10 md:h-10 rounded object-cover flex-shrink-0" />}
        <div className="min-w-0">
          <p className={`font-medium truncate text-sm ${isCurrentlyPlaying ? 'text-[#FF007F]' : 'text-white'}`}>{track.name}</p>
          <p className="text-xs text-gray-400 truncate">{Array.isArray(track.artists) ? track.artists.join(', ') : track.artists}</p>
        </div>
      </div>

      {/* Album (desktop only) + duration (always) */}
      <span className="hidden md:block w-40 text-gray-400 text-sm truncate">{track.album}</span>
      <span className="text-right text-gray-500 text-xs tabular-nums flex-shrink-0 ml-1">{formatDuration(track.duration_ms)}</span>

      {/* Remove button — desktop hover, mobile always-visible icon */}
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => { e.stopPropagation(); onRemove(track.id); }}
        className="md:opacity-0 md:group-hover:opacity-100 opacity-50 hover:bg-red-500/20 h-7 w-7 md:h-8 md:w-8 flex-shrink-0"
        data-testid={`remove-track-${track.id}`}
      >
        <X className="w-3.5 h-3.5 md:w-4 md:h-4 text-red-400" />
      </Button>
    </div>
  );
};

// Compact relative time formatter — "2h ago", "3d ago", "just now"
const formatRelativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 30 * 86400) return `${Math.floor(seconds / 86400)}d ago`;
  if (seconds < 365 * 86400) return `${Math.floor(seconds / (30 * 86400))}mo ago`;
  return `${Math.floor(seconds / (365 * 86400))}y ago`;
};

const PlaylistPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const type = searchParams.get('type') || 'curated'; // 'curated' or 'created'

  const { user } = useAuth();
  const { playTracks, isPlaying, currentTrack, syncQueue } = useMusic();

  const [playlist, setPlaylist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSongs, setShowAddSongs] = useState(false);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchPlaylist = useCallback(async () => {
    try {
      const endpoint = type === 'created'
        ? `${API}/playlists/created/${id}`
        : `${API}/playlists/curated/${id}`;
      const response = await axios.get(endpoint);
      setPlaylist(response.data);
      setTracks(response.data.tracks || []);
    } catch (error) {
      console.error('Failed to fetch playlist:', error);
      toast.error('Failed to load playlist');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, type, navigate]);

  useEffect(() => {
    fetchPlaylist();
  }, [fetchPlaylist]);

  // Save updated tracks to backend
  const saveTrackOrder = async (newTracks) => {
    setSaving(true);
    try {
      if (type === 'created') {
        await axios.put(`${API}/playlists/created/${id}`, { tracks: newTracks });
      } else {
        await axios.put(`${API}/playlists/curated/${id}/tracks`, { tracks: newTracks });
      }
      // Keep the live playback queue in sync if this playlist is currently playing
      syncQueue(newTracks);
    } catch (error) {
      console.error('Failed to save track order:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tracks.findIndex(t => t.id === active.id);
    const newIndex = tracks.findIndex(t => t.id === over.id);
    const newTracks = arrayMove(tracks, oldIndex, newIndex);
    setTracks(newTracks);
    saveTrackOrder(newTracks);
  };

  const handleRemoveTrack = (trackId) => {
    const newTracks = tracks.filter(t => t.id !== trackId);
    setTracks(newTracks);
    saveTrackOrder(newTracks);
    toast.success('Track removed');
  };

  const handleAddTrack = async (track) => {
    if (tracks.some(t => t.id === track.id)) {
      toast.error('Track already in playlist');
      return;
    }

    const newTracks = [...tracks, track];
    setTracks(newTracks);

    try {
      if (type === 'created') {
        await axios.put(`${API}/playlists/created/${id}`, { tracks: newTracks });
      } else {
        await axios.post(`${API}/playlists/curated/${id}/add-track`, { track });
      }
      // Keep the live playback queue in sync if this playlist is currently playing
      syncQueue(newTracks);
    } catch (error) {
      console.error('Failed to add track:', error);
      toast.error('Failed to add track');
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatTotalDuration = (ms) => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (hours > 0) return `${hours} hr ${minutes} min`;
    return `${minutes} min`;
  };

  const handlePlayAll = () => {
    if (!tracks || tracks.length === 0) {
      toast.error('No tracks to play');
      return;
    }
    playTracks(tracks, 0, { playlistId: id });
  };

  const handlePlayTrack = (index) => {
    if (!tracks) return;
    playTracks(tracks, index, { playlistId: id });
  };

  const isTrackPlaying = (track) => currentTrack?.id === track.id && isPlaying;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-white">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!playlist) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-white">Playlist not found</div>
        </div>
      </Layout>
    );
  }

  const totalDuration = playlist.total_duration_ms || tracks.reduce((acc, t) => acc + (t.duration_ms || 0), 0);

  return (
    <Layout>
      <div className="flex flex-col h-full pb-24" data-testid="playlist-page">
        {/* Header — compact mobile layout: small cover + tight title row */}
        <div className="px-3 py-4 md:p-6 lg:p-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          <div className="flex flex-row gap-4 md:gap-6 items-center md:items-start">
            <div className="w-20 h-20 md:w-44 md:h-44 lg:w-56 lg:h-56 rounded-xl md:rounded-2xl overflow-hidden shadow-2xl flex-shrink-0">
              {playlist.cover_image ? (
                <img src={playlist.cover_image} alt={playlist.name || 'Playlist'} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#FF007F] to-[#E11D48] flex items-center justify-center">
                  <Music className="w-8 h-8 md:w-20 md:h-20 text-white/50" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-xl md:text-3xl lg:text-5xl font-bold text-white font-['Outfit'] mb-2 md:mb-4 leading-tight truncate md:whitespace-normal">
                {playlist.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-400 mb-4 md:mb-6">
                <span className="flex items-center gap-1"><Music className="w-3.5 h-3.5 md:w-4 md:h-4" />{tracks.length} songs</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 md:w-4 md:h-4" />{formatTotalDuration(totalDuration)}</span>
                {playlist.last_yt_synced_at && (
                  <span
                    className="hidden md:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#39FF14] font-['JetBrains_Mono'] bg-[#39FF14]/10 border border-[#39FF14]/30 px-2.5 py-1 rounded-full"
                    title={`Last synced at ${new Date(playlist.last_yt_synced_at).toLocaleString()}`}
                    data-testid="playlist-last-synced"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
                    Synced · {formatRelativeTime(playlist.last_yt_synced_at)}
                  </span>
                )}
                {saving && <span className="text-[#FF007F] text-xs">Saving...</span>}
              </div>

              <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                <Button
                  onClick={handlePlayAll}
                  disabled={tracks.length === 0}
                  data-testid="play-all-button"
                  className="h-10 md:h-12 px-6 md:px-8 bg-[#FC3C44] hover:bg-[#E0353D] text-white font-semibold rounded-full transition-all hover:scale-105 disabled:opacity-50 text-sm md:text-base"
                >
                  <Play className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2" fill="currentColor" />
                  Play
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowAddSongs(!showAddSongs)}
                  className={`h-10 md:h-12 px-4 md:px-5 rounded-full border-white/10 text-sm md:text-base ${showAddSongs ? 'bg-[#FC3C44]/20 text-[#FC3C44] border-[#FC3C44]/30' : 'bg-white/5 hover:bg-white/10 text-white'}`}
                  data-testid="add-songs-button"
                >
                  {showAddSongs ? <X className="w-4 h-4 mr-1.5 md:mr-2" /> : <Plus className="w-4 h-4 mr-1.5 md:mr-2" />}
                  {showAddSongs ? 'Close' : 'Add'}
                </Button>
              </div>
            </div>
          </div>

          {/* Add Songs Section */}
          {showAddSongs && (
            <div className="mt-6 p-4 bg-[#121212] border border-white/10 rounded-xl animate-fade-in" data-testid="add-songs-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Search className="w-5 h-5 text-[#FF007F]" />
                  Search & Add Songs
                </h3>
              </div>
              <TrackSearch
                onAddTrack={handleAddTrack}
                existingTrackIds={tracks.map(t => t.id)}
                placeholder="Search Apple Music for songs to add..."
              />
            </div>
          )}
        </div>

        {/* Track List */}
        <div className="flex-1 px-2 md:px-6 lg:px-8 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="track-list">
              {/* Header Row */}
              <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500 border-b border-white/5">
                <span className="hidden md:block w-6" />
                <span className="w-5 md:w-6 text-center">#</span>
                <span className="flex-1">Title</span>
                <span className="hidden md:block w-40">Album</span>
                <span className="text-right ml-1"><Clock className="w-3.5 h-3.5 inline" /></span>
                <span className="w-7 md:w-10" />
              </div>

              {tracks.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No songs in this playlist yet</p>
                  <p className="text-sm mt-2">Click &quot;Add Songs&quot; to start building your playlist</p>
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={tracks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                    {tracks.map((track, index) => (
                      <SortableTrack
                        key={track.id}
                        track={track}
                        index={index}
                        onRemove={handleRemoveTrack}
                        onPlay={handlePlayTrack}
                        isCurrentlyPlaying={isTrackPlaying(track)}
                        formatDuration={formatDuration}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </Layout>
  );
};

export default PlaylistPage;
